import { ColorSpace, convertFromLinear } from "./color-spaces";
import { TwoOffBuffer } from "./two-off-buffer";

const srgbLinearMapping: number[] = [
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    1,
    1,
    1,
    1,
    1,
    1,
    2,
    2,
    2,
    2,
    2,
    2,
    2,
    3,
    3,
    3,
    3,
    3,
    3,
    4,
    4,
    4,
    4,
    4,
    5,
    5,
    5,
    5,
    6,
    6,
    6,
    7,
    7,
    7,
    7,
    8,
    8,
    8,
    9,
    9,
    9,
    10,
    10,
    10,
    11,
    11,
    12,
    12,
    12,
    13,
    13,
    14,
    14,
    15,
    15,
    15,
    16,
    16,
    17,
    17,
    18,
    18,
    19,
    19,
    20,
    21,
    21,
    22,
    22,
    23,
    23,
    24,
    25,
    25,
    26,
    27,
    27,
    28,
    28,
    29,
    30,
    31,
    31,
    32,
    33,
    33,
    34,
    35,
    36,
    36,
    37,
    38,
    39,
    40,
    40,
    41,
    42,
    43,
    44,
    45,
    45,
    46,
    47,
    48,
    49,
    50,
    51,
    52,
    53,
    54,
    55,
    56,
    57,
    58,
    59,
    60,
    61,
    62,
    63,
    64,
    65,
    66,
    67,
    68,
    69,
    71,
    72,
    73,
    74,
    75,
    76,
    78,
    79,
    80,
    81,
    82,
    84,
    85,
    86,
    87,
    89,
    90,
    91,
    93,
    94,
    95,
    97,
    98,
    100,
    101,
    102,
    104,
    105,
    107,
    108,
    109,
    111,
    112,
    114,
    115,
    117,
    118,
    120,
    122,
    123,
    125,
    126,
    128,
    130,
    131,
    133,
    134,
    136,
    138,
    139,
    141,
    143,
    145,
    146,
    148,
    150,
    152,
    153,
    155,
    157,
    159,
    161,
    162,
    164,
    166,
    168,
    170,
    172,
    174,
    176,
    178,
    179,
    181,
    183,
    185,
    187,
    189,
    191,
    193,
    196,
    198,
    200,
    202,
    204,
    206,
    208,
    210,
    212,
    215,
    217,
    219,
    221,
    223,
    226,
    228,
    230,
    232,
    235,
    237,
    239,
    242,
    244,
    246,
    249,
    251,
    253,
    255,
    255,
    255,
    255,
    255,
    255,
    255,
    255,
    255,
    255,
    255,
    255,
    255,
    255,
];

function grayscaleFromRGBALinear(r: number, g: number, b: number, a: number): number {
    return (((0.299 * r + 0.587 * g + 0.114 * b) * a) / 255) | 0;
}

function grayscaleFromRGBAsRGB(r: number, g: number, b: number, a: number): number {
    return grayscaleFromRGBALinear(srgbLinearMapping[r], srgbLinearMapping[g], srgbLinearMapping[b], a);
}

export function grayscaleFromRGBA(colorSpace: ColorSpace, r: number, g: number, b: number, a: number): number {
    if (colorSpace === ColorSpace.Linear) {
        return grayscaleFromRGBALinear(r, g, b, a);
    } else {
        return grayscaleFromRGBAsRGB(r, g, b, a);
    }
}

function grayscaleTwoOffLinear(
    src: Uint8Array,
    data: Int32Array,
    dstWidth: number,
    dstHeight: number,
    srcWidth: number,
    srcXOffset: number,
    srcYOffset: number
) {
    const bufWidth = dstWidth + 2;
    let yOffset: number, initialSrcOffset: number, srcOffset: number;
    for (let y = 0; y < dstHeight; y++) {
        yOffset = (y + 1) * bufWidth;
        data[yOffset] = 0;
        initialSrcOffset = (srcYOffset + y) * srcWidth + srcXOffset;
        yOffset += 1;
        for (let x = 0; x < dstWidth; x++) {
            srcOffset = (initialSrcOffset + x) * 4;
            data[yOffset + x] = grayscaleFromRGBALinear(
                src[srcOffset],
                src[srcOffset + 1],
                src[srcOffset + 2],
                src[srcOffset + 3]
            );
        }
        data[yOffset + dstWidth] = 0;
    }
}

function grayscaleTwoOffsRGB(
    src: Uint8Array,
    data: Int32Array,
    dstWidth: number,
    dstHeight: number,
    srcWidth: number,
    srcXOffset: number,
    srcYOffset: number
) {
    const bufWidth = dstWidth + 2;
    let yOffset: number, initialSrcOffset: number, srcOffset: number;
    for (let y = 0; y < dstHeight; y++) {
        yOffset = (y + 1) * bufWidth;
        data[yOffset] = 0;
        initialSrcOffset = (srcYOffset + y) * srcWidth + srcXOffset;
        yOffset += 1;
        for (let x = 0; x < dstWidth; x++) {
            srcOffset = (initialSrcOffset + x) * 4;
            data[yOffset + x] = grayscaleFromRGBAsRGB(
                src[srcOffset],
                src[srcOffset + 1],
                src[srcOffset + 2],
                src[srcOffset + 3]
            );
        }
        data[yOffset + dstWidth] = 0;
    }
}

export function grayscaleIntoTwoOff(
    colorSpace: ColorSpace,
    src: Uint8Array,
    dst: TwoOffBuffer,
    dstWidth: number,
    dstHeight: number,
    srcWidth: number = dstWidth,
    srcXOffset: number = 0,
    srcYOffset: number = 0
) {
    dstWidth = Math.floor(dstWidth);
    dstHeight = Math.floor(dstHeight);
    srcWidth = Math.floor(srcWidth);

    if (dstWidth > dst.width || dstHeight > dst.height) {
        throw new TypeError("Destination buffer could not fit image");
    }

    const { _backing: data } = dst;
    const bufWidth = dstWidth + 2;
    const lastLineY = bufWidth * (dstHeight + 1);

    for (let x = -1; x <= dstWidth; x++) {
        data[x + 1] = 0;
    }

    for (let x = -1; x <= dstWidth; x++) {
        data[lastLineY + x + 1] = 0;
    }

    if (colorSpace === ColorSpace.Linear) {
        grayscaleTwoOffLinear(src, data, dstWidth, dstHeight, srcWidth, srcXOffset, srcYOffset);
    } else {
        grayscaleTwoOffsRGB(src, data, dstWidth, dstHeight, srcWidth, srcXOffset, srcYOffset);
    }
}

export function grayscaleFromTwoOff(
    colorSpace: ColorSpace,
    src: TwoOffBuffer,
    dst: Uint8Array,
    width: number,
    height: number
) {
    width = Math.floor(width);
    height = Math.floor(height);

    if (width < src.width || height < src.height) {
        throw new TypeError("Destination buffer could not fit binary image");
    }

    for (let y = 0; y < height; y++) {
        const dstYOffset = y * width * 4;
        for (let x = 0; x < width; x++) {
            const dstOffset = dstYOffset + x * 4;
            const luma = convertFromLinear(colorSpace, src.get(x, y));
            dst[dstOffset] = luma;
            dst[dstOffset + 1] = luma;
            dst[dstOffset + 2] = luma;
            dst[dstOffset + 3] = 255;
        }
    }
}
